import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";

const GROUPS = [
  {
    title: "Pages",
    items: [
      ["Home", "/"],
      ["About Us", "/about"],
      ["Artificial Intelligence", "/ai", "ai"],
      ["Industry 4.0", "/industry-4.0", "iot smart factory"],
      ["Quality Assurance", "/quality-assurance", "qa testing"],
      ["Agentic AI", "/agentic-ai", "agents"],
      ["Robotic Process Automation", "/robotic-process-automation", "rpa"],
      ["Blog", "/blogs", "articles news"],
      ["Privacy Policy", "/privacy-policy/"]
    ]
  },
  {
    title: "Solutions",
    items: [
      ["AI Daily Report Management Information System (DRMIS)", "/solutions/ai-drmis/"],
      ["AI Stores Management", "/solutions/ai-stores-management/"],
      ["AI Supplier Identification", "/solutions/ai-supplier-identification/"],
      ["Logistics Gate Automation", "/solutions/logistics-gate-automation/"],
      ["AI for SCADA Analytics", "/solutions/ai-scada-analytics/"],
      ["Supply Chain Portal", "/solutions/supply-chain-portal/"]
    ]
  },
  {
    title: "On the home page",
    items: [
      ["What We Transform", "/#challenges"],
      ["How We Transform", "/#solutions"],
      ["18 Departments", "/#departments"],
      ["AI and Automation Assessment", "/#consulting"],
      ["Case Studies", "/#case-studies"],
      ["Why SurensInffotek", "/#why-surens"],
      ["Assistance Now", "/#cta"]
    ]
  }
];

const displayUrl = (href) => `surensinffotek.com${href === "/" ? "" : href}`;

const matches = ([label, , keywords = ""], words) => {
  const terms = `${label} ${keywords}`.toLowerCase().split(/[^a-z0-9.]+/);
  return words.every((word) => terms.some((term) => term.startsWith(word)));
};

export default function SearchPage() {
  const [params] = useSearchParams();
  const [query, setQuery] = useState(() => params.get("q") || "");

  const groups = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return GROUPS.map((group) => ({
      ...group,
      items: group.items.filter((item) => matches(item, words))
    })).filter((group) => group.items.length);
  }, [query]);

  const total = groups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <>
      <Helmet>
        <title>Search | SurensInffotek</title>
        <meta
          name="description"
          content="Search SurensInffotek pages for AI, automation, Industry 4.0, quality assurance, solutions, and the blog."
        />
        <link rel="canonical" href="https://surensinffotek.com/search/" />
      </Helmet>
      <main className="si-page">
        <header className="si-intro">
          <div className="si-wrap">
            <h1 className="si-title">Search</h1>
            <form className="si-search" role="search" onSubmit={(event) => event.preventDefault()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
              </svg>
              <input
                id="siteSearch"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search solutions, services and pages"
                aria-label="Search the site"
                className="si-input"
              />
            </form>
            <p className="si-count">
              {query.trim()
                ? `${total} ${total === 1 ? "result" : "results"} for “${query.trim()}”`
                : `${total} pages on this site`}
            </p>
          </div>
        </header>

        <div className="si-wrap">
          {groups.length ? (
            groups.map((group) => (
              <section className="si-group" key={group.title}>
                <h2 className="si-col-title">{group.title}</h2>
                <ul className="si-hits">
                  {group.items.map(([label, href]) => (
                    <li key={href}>
                      <a href={href}>
                        <span className="si-hit-title">{label}</span>
                        <span className="si-hit-url">{displayUrl(href)}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <p className="si-empty">
              Nothing matched “{query.trim()}”. Try a shorter word, such as “AI”, “stores” or “quality”.
            </p>
          )}
          <div style={{ height: "4rem" }} />
        </div>
      </main>
    </>
  );
}
